import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { TabBar } from './components/TabBar.tsx';
import { Home } from './screens/Home.tsx';
import { Book } from './screens/Book.tsx';
import { Partner } from './screens/Partner.tsx';
import { Diary } from './screens/Diary.tsx';
import { Room } from './screens/Room.tsx';
import { Login, NoServer, SigningIn } from './screens/Login.tsx';
import { liveStatus, onSignedOut } from './api/client.ts';
import { finishKakaoLogin, hasAuthCode, startKakaoLogin } from './api/auth.ts';

/**
 * 웹뷰 · 웹앱 공통 셸.
 *
 * 로그인이 들어오는 길이 둘입니다.
 *
 *   네이티브 셸   셸이 카카오 로그인을 끝내고 토큰을 심어 줍니다.
 *                 여기 들어올 때 이미 `live` 이므로 아래 관문을 그냥 지납니다.
 *   브라우저      셸이 없으므로 이 앱이 직접 로그인합니다 (화면 A1).
 *
 * 무료 계정으로 서명한 IPA 는 7일마다 다시 깔아야 하지만 홈 화면에 추가한
 * 웹앱은 만료가 없습니다. 그래서 브라우저 쪽 길을 «임시 수단»이 아니라
 * 제대로 갖춥니다.
 */

/** 서버 주소가 없는 빌드에서만 쓰는 탈출구 — 예시 데이터로 둘러보기 */
type Gate = 'checking' | 'login' | 'signing-in' | 'preview' | 'app';

export function App() {
  const [gate, setGate] = useState<Gate>(() => {
    // 카카오에서 돌아온 주소라면 코드부터 처리합니다. 로그인 화면을 한 번
    // 깜빡이고 나서 처리하면 사용자가 «또 누르라는 건가» 하게 됩니다
    if (hasAuthCode()) return 'signing-in';
    return 'checking';
  });
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (gate !== 'signing-in') return;
    let alive = true;
    finishKakaoLogin()
      .then(() => {
        if (alive) setGate('app');
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setFailure(e instanceof Error ? e.message : '로그인에 실패했습니다.');
        setGate('login');
      });
    return () => {
      alive = false;
    };
  }, [gate]);

  /**
   * 토큰이 사라지면 로그인 화면으로 돌아옵니다.
   *
   * 30일이 지나 만료되거나 로그아웃을 누른 경우입니다. 셸이 있을 때는 셸이
   * 하던 일인데, 브라우저에는 그럴 셸이 없어 여기서 받습니다 — 알리지 않으면
   * 화면이 오류만 뱉으며 남아 새로고침해야 돌아옵니다.
   */
  useEffect(
    () =>
      onSignedOut(() => {
        setFailure(null);
        // 서버 주소가 없는 빌드는 애초에 로그인이 뜻이 없습니다
        setGate(liveStatus() === 'no-api' ? 'preview' : 'login');
      }),
    [],
  );

  useEffect(() => {
    if (gate !== 'checking') return;
    const status = liveStatus();
    // no-api 는 로그인해도 갈 곳이 없는 빌드입니다. 막다른 로그인 화면 대신
    // 무엇이 빠졌는지 적고 미리보기로 들여보냅니다
    setGate(status === 'live' ? 'app' : status === 'no-api' ? 'preview' : 'login');
  }, [gate]);

  if (gate === 'checking') return null;
  if (gate === 'signing-in') return <SigningIn />;

  if (gate === 'login') {
    return (
      <Login
        busy={false}
        failure={failure}
        onKakao={() => {
          setFailure(null);
          startKakaoLogin();
        }}
      />
    );
  }

  if (gate === 'preview') {
    return <NoServer onPreview={() => setGate('app')} />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/book" element={<Book />} />
        <Route path="/partner" element={<Partner />} />
        <Route path="/diary" element={<Diary />} />
        <Route path="/room" element={<Room />} />
        {/* 알 수 없는 경로는 조용히 Home 으로 — 웹뷰에는 주소창이 없어서
            사용자가 404 를 만나도 돌아갈 방법이 없습니다. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <TabBar />
    </>
  );
}
